import { useEffect, useState } from "preact/hooks"

import type { WidgetConfig } from "@workspace/contracts/embed"

import { partClass } from "../appearance"
import type { SurfaceProps } from "../surface"

/**
 * The testimonial: a card along the bottom, one quote at a time, the dots
 * saying which - the preview's styles number for number, turning on the
 * preview's own clock (a quote every 3.6 s).
 */

type TestimonialConfig = Extract<WidgetConfig, { type: "testimonial" }>

const TURN_MS = 3600

export function TestimonialSurface({ payload, look, open }: SurfaceProps) {
  const config = payload.config as TestimonialConfig
  const { ink, accent, surfaceBg, rad } = look
  const quotes = config.quotes
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (quotes.length < 2) return
    const id = window.setInterval(() => setTick((t) => t + 1), TURN_MS)
    return () => window.clearInterval(id)
  }, [quotes.length])

  if (!open || quotes.length === 0) return null
  const at = tick % quotes.length
  const quote = quotes[at]!

  return (
    <div
      class="uq-fixed uq-fade"
      style={{
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        padding: 24,
        pointerEvents: "none",
      }}
    >
      <figure
        class={partClass(config, "frame")}
        style={{
          width: "100%",
          maxWidth: 440,
          background: surfaceBg,
          border: `1px solid rgba(${ink},.12)`,
          borderLeft: `3px solid ${accent}`,
          borderRadius: rad,
          padding: "16px 18px",
          display: "flex",
          flexDirection: "column",
          gap: 9,
          boxShadow: "0 14px 34px rgba(0,0,0,.12)",
          pointerEvents: "auto",
        }}
      >
        <blockquote
          key={at}
          class={partClass(config, "quote", "uq-fade")}
          style={{ fontSize: 13.5, lineHeight: 1.55, color: `rgb(${ink})` }}
        >
          “{quote.text || "…"}”
        </blockquote>
        <figcaption style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            class={partClass(config, "author")}
            style={{
              fontFamily: "monospace",
              fontSize: 11,
              color: `rgba(${ink},.55)`,
            }}
          >
            {quote.author || "—"}
          </span>
          <span
            class={partClass(config, "dots")}
            style={{ marginLeft: "auto", display: "flex", gap: 4 }}
          >
            {quotes.map((_, i) => (
              <span
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: i === at ? accent : `rgba(${ink},.18)`,
                }}
              />
            ))}
          </span>
        </figcaption>
      </figure>
    </div>
  )
}
